/**
 * Configuración declarativa del Estado de Resultados (P&L).
 *
 * Esta estructura define cómo se presentan las filas devueltas por
 * SP_EF_DATA_FINAL, agrupándolas en secciones expandibles y asignando
 * nivel visual (total / detalle) y formato numérico.
 *
 * Diseñada para ser escalable: agregar una nueva cuenta solo requiere
 * añadir una entrada; agregar una sección solo requiere su id y label.
 */

export type PnLFormat = 'currency' | 'number' | 'ratio';

export interface PnLSection {
  id: string;
  label: string;
  /** Orden de aparición en el árbol. */
  order: number;
  /** Estado inicial de expansión. */
  defaultExpanded?: boolean;
}

export interface PnLRowConfig {
  /** Match exacto con row.Cuenta proveniente del SP. */
  cuenta: string;
  /** Texto visible. Si no se indica, se usa cuenta. */
  label?: string;
  /** Sección a la que pertenece. */
  section: string;
  /** Nivel de indentación/destaque. 0 = KPI/resumen, 1 = subtotal, 2 = detalle. */
  level: 0 | 1 | 2;
  /** Formato numérico. */
  format: PnLFormat;
  /** Si es un total/subtotal que debe resaltarse. */
  isTotal?: boolean;
  /** Si debe considerarse como KPI ejecutivo. */
  isKpi?: boolean;
  /** Orden relativo dentro de la sección. */
  orderInSection: number;
}

export const pnlSections: PnLSection[] = [
  { id: 'kpi', label: 'Indicadores Clave', order: 0, defaultExpanded: true },
  { id: 'volumen', label: 'Volumen', order: 1, defaultExpanded: true },
  { id: 'ventas', label: 'Ventas', order: 2, defaultExpanded: true },
  { id: 'utilidad-bruta', label: 'Utilidad Bruta', order: 3, defaultExpanded: true },
  { id: 'margen-contribucion', label: 'Margen de Contribución', order: 4, defaultExpanded: true },
  { id: 'gastos-operacion', label: 'Gastos de Operación', order: 5, defaultExpanded: false },
  { id: 'resultados', label: 'Resultados', order: 6, defaultExpanded: true },
];

export const pnlRows: PnLRowConfig[] = [
  // ───────────────────────────────────────────────────────────────── KPIs
  {
    cuenta: 'TOTAL_VOLUMENES',
    section: 'kpi',
    level: 0,
    format: 'number',
    isKpi: true,
    orderInSection: 1,
  },
  {
    cuenta: 'VENTAS_NETAS',
    section: 'kpi',
    level: 0,
    format: 'currency',
    isKpi: true,
    orderInSection: 2,
  },
  {
    cuenta: 'VALOR_AGREGADO_TOTAL',
    section: 'kpi',
    level: 0,
    format: 'ratio',
    isKpi: true,
    orderInSection: 3,
  },
  {
    cuenta: 'COLON_KG_PROMEDIO',
    label: 'COLÓN/KG PROMEDIO',
    section: 'kpi',
    level: 0,
    format: 'ratio',
    isKpi: true,
    orderInSection: 4,
  },

  // ───────────────────────────────────────────────────────────────── Volumen
  {
    cuenta: 'VOLUMEN PRODUCCION',
    section: 'volumen',
    level: 2,
    format: 'number',
    orderInSection: 1,
  },
  {
    cuenta: 'VOLUMEN VENTA NACIONAL',
    section: 'volumen',
    level: 2,
    format: 'number',
    orderInSection: 2,
  },
  {
    cuenta: 'VOLUMEN VENTA EXPORTACIONES REGIONAL',
    section: 'volumen',
    level: 2,
    format: 'number',
    orderInSection: 3,
  },
  {
    cuenta: 'VOLUMEN VENTA EXPORTACIONES TZUMI',
    section: 'volumen',
    level: 2,
    format: 'number',
    orderInSection: 4,
  },
  {
    cuenta: 'VOLUMEN VENTA EXPORTACIONES O&T',
    section: 'volumen',
    level: 2,
    format: 'number',
    orderInSection: 5,
  },

  // ───────────────────────────────────────────────────────────────── Ventas
  {
    cuenta: 'VENTAS NACIONALES',
    section: 'ventas',
    level: 2,
    format: 'currency',
    orderInSection: 1,
  },
  {
    cuenta: 'COLON/KG NACIONAL',
    section: 'ventas',
    level: 2,
    format: 'ratio',
    orderInSection: 2,
  },
  {
    cuenta: 'VENTAS EXPORTACIONES-REGIONALES',
    section: 'ventas',
    level: 2,
    format: 'currency',
    orderInSection: 3,
  },
  {
    cuenta: 'VENTAS EXPORTACIONES-TZUMI',
    section: 'ventas',
    level: 2,
    format: 'currency',
    orderInSection: 4,
  },
  {
    cuenta: 'VENTAS EXPORTACIONES-PRICESMART',
    section: 'ventas',
    level: 2,
    format: 'currency',
    orderInSection: 5,
  },
  {
    cuenta: 'VENTAS EXPORTACIONES-O&T GROUP',
    section: 'ventas',
    level: 2,
    format: 'currency',
    orderInSection: 6,
  },
  {
    cuenta: 'VENTAS EXPORTACIONES-REGIONAL',
    section: 'ventas',
    level: 1,
    format: 'currency',
    isTotal: true,
    orderInSection: 7,
  },
  {
    cuenta: 'COLON/KG - EXPORTACIONES - REGIONALES',
    section: 'ventas',
    level: 2,
    format: 'ratio',
    orderInSection: 8,
  },
  {
    cuenta: 'COLON/KG - EXPORTACIONES - TZUMI',
    section: 'ventas',
    level: 2,
    format: 'ratio',
    orderInSection: 9,
  },
  {
    cuenta: 'COLON/KG - PROMEDIO',
    section: 'ventas',
    level: 1,
    format: 'ratio',
    isTotal: true,
    orderInSection: 10,
  },

  // ───────────────────────────────────────────────────────────────── Utilidad Bruta
  {
    cuenta: 'VENTAS NETAS',
    label: 'VENTAS NETAS',
    section: 'utilidad-bruta',
    level: 1,
    format: 'currency',
    isTotal: true,
    orderInSection: 1,
  },
  {
    cuenta: 'COSTO DE VENTAS',
    section: 'utilidad-bruta',
    level: 2,
    format: 'currency',
    orderInSection: 2,
  },
  {
    cuenta: 'COSTO/KG PRODUCIDO',
    section: 'utilidad-bruta',
    level: 2,
    format: 'ratio',
    orderInSection: 3,
  },
  {
    cuenta: 'VALOR AGREGADO - TOTAL/KG',
    section: 'utilidad-bruta',
    level: 2,
    format: 'ratio',
    orderInSection: 4,
  },
  {
    cuenta: 'VALOR AGREGADO KG/NACIONAL',
    section: 'utilidad-bruta',
    level: 2,
    format: 'ratio',
    orderInSection: 5,
  },
  {
    cuenta: 'VALOR AGREGADO KG/EXPO-REGIONAL',
    section: 'utilidad-bruta',
    level: 2,
    format: 'ratio',
    orderInSection: 6,
  },
  {
    cuenta: 'VALOR AGREGADO KG/EXPO-TZUMI',
    section: 'utilidad-bruta',
    level: 2,
    format: 'ratio',
    orderInSection: 7,
  },
  {
    cuenta: 'UTILIDAD BRUTA',
    section: 'utilidad-bruta',
    level: 1,
    format: 'currency',
    isTotal: true,
    orderInSection: 8,
  },

  // ───────────────────────────────────────────────────────────────── Margen de Contribución
  {
    cuenta: 'GASTOS VARIABLES LOGÍSTICA ALMACENES',
    section: 'margen-contribucion',
    level: 2,
    format: 'currency',
    orderInSection: 1,
  },
  {
    cuenta: 'GASTOS VARIABLES DISTRIBUCIÓN NACIONAL',
    section: 'margen-contribucion',
    level: 2,
    format: 'currency',
    orderInSection: 2,
  },
  {
    cuenta: 'GASTOS VARIABLES DISTRIBUCIÓN EXTERIOR',
    section: 'margen-contribucion',
    level: 2,
    format: 'currency',
    orderInSection: 3,
  },
  {
    cuenta: 'MARGEN CONTRIBUCIÓN NACIONAL',
    section: 'margen-contribucion',
    level: 2,
    format: 'currency',
    orderInSection: 4,
  },
  {
    cuenta: 'MC/KG NACIONAL',
    section: 'margen-contribucion',
    level: 2,
    format: 'ratio',
    orderInSection: 5,
  },
  {
    cuenta: 'MARGEN CONTRIBUCIÓN EXTERIOR',
    section: 'margen-contribucion',
    level: 2,
    format: 'currency',
    orderInSection: 6,
  },
  {
    cuenta: 'MC/KG EXTERIOR',
    section: 'margen-contribucion',
    level: 2,
    format: 'ratio',
    orderInSection: 7,
  },
  {
    cuenta: 'MARGEN CONTRIBUCIÓN EXTERIOR - TZUMI',
    section: 'margen-contribucion',
    level: 2,
    format: 'currency',
    orderInSection: 8,
  },
  {
    cuenta: 'MC/KG TZUMI',
    section: 'margen-contribucion',
    level: 2,
    format: 'ratio',
    orderInSection: 9,
  },
  {
    cuenta: 'MARGEN CONTRIBUCIÓN',
    section: 'margen-contribucion',
    level: 1,
    format: 'currency',
    isTotal: true,
    orderInSection: 10,
  },

  // ───────────────────────────────────────────────────────────────── Gastos de Operación
  // Las cuentas de gastos vienen dinámicas del SP; se renderizan tal cual.
  // No las predefinimos para no acoplar el frontend a un listado fijo.

  {
    cuenta: 'TOTAL GASTOS OPERACIÓN',
    section: 'gastos-operacion',
    level: 1,
    format: 'currency',
    isTotal: true,
    orderInSection: 999,
  },
  {
    cuenta: 'DEPRECIACIONES',
    section: 'gastos-operacion',
    level: 2,
    format: 'currency',
    orderInSection: 1000,
  },
  {
    cuenta: 'AMORTIZACIONES',
    section: 'gastos-operacion',
    level: 2,
    format: 'currency',
    orderInSection: 1001,
  },

  // ───────────────────────────────────────────────────────────────── Resultados
  {
    cuenta: 'EBITDA',
    section: 'resultados',
    level: 1,
    format: 'currency',
    isTotal: true,
    orderInSection: 1,
  },
  {
    cuenta: 'UTILIDAD EN OPERACIÓN',
    section: 'resultados',
    level: 1,
    format: 'currency',
    isTotal: true,
    orderInSection: 2,
  },
  {
    cuenta: 'INGRESOS Y GASTOS FINANCIEROS NETO',
    section: 'resultados',
    level: 2,
    format: 'currency',
    orderInSection: 3,
  },
  {
    cuenta: 'UTILIDAD ANTES DE IMPUESTOS',
    section: 'resultados',
    level: 1,
    format: 'currency',
    isTotal: true,
    orderInSection: 4,
  },
  {
    cuenta: 'IMPUESTO DE RENTA',
    section: 'resultados',
    level: 2,
    format: 'currency',
    orderInSection: 5,
  },
  {
    cuenta: 'UTILIDAD NETA',
    section: 'resultados',
    level: 1,
    format: 'currency',
    isTotal: true,
    orderInSection: 6,
  },
];

/** Mapa rápido por cuenta. */
export const pnlRowByCuenta = new Map<string, PnLRowConfig>(
  pnlRows.map(r => [r.cuenta, r])
);

/** Mapa rápido por sección. */
export const pnlSectionById = new Map<string, PnLSection>(
  pnlSections.map(s => [s.id, s])
);
